import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, SemanticError, SemanticSuccess } from '../../../../core-components/theme/color-palette/colors';

import type { PasswordRuleState } from '../../domain/newPasswordRules';

import { passwordRuleText } from './passwordRuleText';

const RULE_COLOR: Readonly<Record<PasswordRuleState, string>> = {
  pending: Neutrals[600],
  met: SemanticSuccess[900],
  failed: SemanticError[600],
};

const lineSx = (state: PasswordRuleState): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  color: RULE_COLOR[state],
});

const iconSx: SystemStyleObject<Theme> = { flexShrink: 0 };

const RuleIcon: React.FC<{ state: PasswordRuleState }> = ({ state }) => {
  if (state === 'failed') return <ErrorOutlineIcon fontSize="small" aria-hidden sx={iconSx} />;
  if (state === 'met') return <CheckCircleOutlineIcon fontSize="small" aria-hidden sx={iconSx} />;
  return null;
};

export interface PasswordRuleLineProps {
  readonly id: string;
  readonly state: PasswordRuleState;
  readonly minLength: number;
}

export const PasswordRuleLine: React.FC<PasswordRuleLineProps> = ({ id, state, minLength }) => {
  const { t } = useTranslation();
  return (
    <Box sx={lineSx(state)}>
      <RuleIcon state={state} />
      <Typography id={id} variant="label" component="p" color="inherit">
        {passwordRuleText(t, state, minLength)}
      </Typography>
    </Box>
  );
};
